import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import TopNavigation from './TopNavigation';
import BottomNavigation from './BottomNavigation';
import SidebarFilters from './SidebarFilters';
import TrendingSection from './TrendingSection';
import PostCreationBox from './PostCreationBox';
import PostFilterNav from './PostFilterNav';
import PostFeed from './PostFeed';
import CetarusLoginAnimation from './CetarusLoginAnimation';
import Onboarding from './Onboarding';
import { useOnboarding } from '../hooks/useOnboarding';
import './Home.css';

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedHacks, setSelectedHacks] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBiomarkers, setSelectedBiomarkers] = useState([]);
  const [selectedBiomarkerCategories, setSelectedBiomarkerCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState('latest');
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showIntroOverlay, setShowIntroOverlay] = useState(Boolean(location.state?.showIntroAnimation));
  const [animationCycleComplete, setAnimationCycleComplete] = useState(false);
  const [bootstrapComplete, setBootstrapComplete] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  
  // Use centralized onboarding hook
  const [showOnboarding, setShowOnboarding] = useOnboarding({
    shouldCheck: !showIntroOverlay && bootstrapComplete && !!currentUser,
    userId: currentUser?.id
  });
  const postFeedRef = useRef(null);

  const handleIntroAnimationComplete = useCallback(() => {
    setAnimationCycleComplete(true);
  }, []);

  const mainContainerStyle = useMemo(
    () => ({
      opacity: showIntroOverlay ? 0 : 1,
      pointerEvents: showIntroOverlay ? 'none' : 'auto',
      transition: 'opacity 260ms ease-in-out'
    }),
    [showIntroOverlay]
  );

  useEffect(() => {
    if (location.state?.showIntroAnimation) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handlePostCreated = (newPost) => {
    // Add the new post directly to the feed if provided
    if (newPost && postFeedRef.current) {
      postFeedRef.current.addNewPost(newPost);
    } else if (postFeedRef.current) {
      // Fallback to full refresh if post data not provided
      postFeedRef.current.refresh();
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  // Prevent scroll event bubbling from sidebar to main content
  useEffect(() => {
    const handleSidebarScroll = (e) => {
      e.stopPropagation();
    };

    const sidebarElement = sidebarRef.current;
    if (sidebarElement) {
      sidebarElement.addEventListener('wheel', handleSidebarScroll, { passive: false });
      sidebarElement.addEventListener('scroll', handleSidebarScroll, { passive: false });
    }

    return () => {
      if (sidebarElement) {
        sidebarElement.removeEventListener('wheel', handleSidebarScroll);
        sidebarElement.removeEventListener('scroll', handleSidebarScroll);
      }
    };
  }, []);



  // Fetch current user profile data
  useEffect(() => {
    let isMounted = true;

    async function fetchCurrentUserProfile() {
      try {
        // Use session instead of auth.getUser() for better performance
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          if (!isMounted) return;
          setCurrentUser(session.user);
          
          // Check if we have cached profile data
          const cachedProfile = sessionStorage.getItem(`userProfile_${session.user.id}`);
          if (cachedProfile) {
            try {
              const parsedProfile = JSON.parse(cachedProfile);
              if (isMounted) {
                setUserProfile(parsedProfile);
              }
              return; // Use cached data
            } catch (e) {
              // Invalid cache, continue to fetch
            }
          }
          
          // Fetch user profile data - use deduplication to prevent duplicate requests
          // Use same cache key as TopNavigation to share the request
          const cacheKey = getCacheKey('fetchUserProfile', session.user.id);
          const profileData = await dedupeRequest(cacheKey, async () => {
            const { data } = await supabase
              .from('user_profiles')
              .select('username, profile_picture_url, gender, onboarding_completed')
              .eq('user_id', session.user.id)
              .maybeSingle();
            return data;
          }, { keepAliveMs: 2000, caller: 'Home' });
          
          if (!isMounted) {
            return;
          }

          if (profileData) {
            const userProfileData = {
              username: profileData.username || '',
              profilePictureUrl: profileData.profile_picture_url || '',
              gender: profileData.gender || null
            };
            setUserProfile(userProfileData);
            
            // Cache the profile data (including gender for BiomarkerSelector)
            sessionStorage.setItem(`userProfile_${session.user.id}`, JSON.stringify(userProfileData));
          } else {
            // Cache empty profile data as fallback
            const fallbackProfile = {
              username: '',
              profilePictureUrl: '',
              gender: null
            };
            sessionStorage.setItem(`userProfile_${session.user.id}`, JSON.stringify(fallbackProfile));
          }
        }
      } finally {
        if (isMounted) {
          setBootstrapComplete(true);
        }
      }
    }
    
    fetchCurrentUserProfile();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Listen for profile updates to keep cache fresh
  useEffect(() => {
    if (!currentUser) return;
    
    let channel;
    
    const setupProfileListener = async () => {
      channel = supabase
        .channel('profile_updates_home')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'user_profiles',
            filter: `user_id=eq.${currentUser.id}`
          },
          (payload) => {
            if (payload.new.profile_picture_url !== payload.old.profile_picture_url) {
              // Update cache and state
              const updatedProfile = {
                ...userProfile,
                profilePictureUrl: payload.new.profile_picture_url || ''
              };
              setUserProfile(updatedProfile);
              sessionStorage.setItem(`userProfile_${currentUser.id}`, JSON.stringify(updatedProfile));
            }
          }
        )
        .subscribe();
    };

    setupProfileListener();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (!showIntroOverlay) {
      return undefined;
    }

    if (animationCycleComplete && bootstrapComplete) {
      const timeout = setTimeout(() => {
        setShowIntroOverlay(false);
      }, 120);
      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [animationCycleComplete, bootstrapComplete, showIntroOverlay]);


  const shouldShowLoadingIndicator = animationCycleComplete && !bootstrapComplete;

  return (
    <>
      {showIntroOverlay && (
        <CetarusLoginAnimation
          onAnimationComplete={handleIntroAnimationComplete}
          showLoadingIndicator={shouldShowLoadingIndicator}
          loadingMessage="Loading your feed..."
          backgroundVariant="signin"
        />
      )}
      <div className="home-main-container" style={mainContainerStyle}>
        <TopNavigation activePage="home" />
        <div className="home-main-content">
          {/* Mobile Sidebar Overlay */}
          {isSidebarOpen && (
            <div 
              className="mobile-sidebar-overlay"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          <aside 
            className={`home-sidebar ${isSidebarOpen ? 'mobile-open' : ''}`} 
            ref={sidebarRef}
          >
            <div 
              className="mobile-sidebar-close-handle"
              onClick={() => setIsSidebarOpen(false)}
            >
              <div className="handle-bar"></div>
            </div>
            <div className="sidebar-content-wrapper">
              <SidebarFilters
                selectedHacks={selectedHacks}
                setSelectedHacks={setSelectedHacks}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedBiomarkers={selectedBiomarkers}
                setSelectedBiomarkers={setSelectedBiomarkers}
                selectedBiomarkerCategories={selectedBiomarkerCategories}
                setSelectedBiomarkerCategories={setSelectedBiomarkerCategories}
              />
              <div className="sidebar-footer">
                <span className="sidebar-footer-text">Cetarus™</span>
              </div>
            </div>
          </aside>
          <main className="home-main">
            <div className="main-center-container">
              {/* Post Creation Box moved to top */}
              <section className="home-post-create-section">
                <div className="post-creation-box" data-onboarding="post-creation">
                  <PostCreationBox
                    onPostCreated={handlePostCreated}
                    userProfile={userProfile}
                  />
                </div>
              </section>
              <section className="home-filter-section">
                <PostFilterNav
                  activeFilter={activeFilter}
                  onFilterChange={handleFilterChange}
                  onSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
                />
              </section>
              <section className="home-feed-section">
                <PostFeed
                  ref={postFeedRef}
                  selectedHacks={selectedHacks}
                  selectedCategories={selectedCategories}
                  selectedBiomarkers={selectedBiomarkers}
                  selectedBiomarkerCategories={selectedBiomarkerCategories}
                  activeFilter={activeFilter}
                />
              </section>
            </div>
          </main>
          {/* Fixed Trending Card on Right Side + Footer below */}
          <div className="home-trending-column">
            <aside className="home-trending-card">
              <TrendingSection />
            </aside>
            <footer className="home-trending-footer">
              <span className="home-trending-footer-copy">Cetarus © 2026</span>
              <nav className="home-trending-footer-links" aria-label="Legal and settings">
                <div className="home-trending-footer-row">
                  <Link to="/about" className="home-trending-footer-link">About</Link>
                  <span className="home-trending-footer-sep">·</span>
                  <Link to="/terms-of-service" className="home-trending-footer-link">Terms & Conditions</Link>
                </div>
                <div className="home-trending-footer-row">
                  <Link to="/privacy-policy" className="home-trending-footer-link">Privacy</Link>
                  <span className="home-trending-footer-sep">·</span>
                  <Link to="/account" className="home-trending-footer-link">Settings</Link>
                </div>
              </nav>
            </footer>
          </div>
        </div>
      </div>
      <BottomNavigation />
      {showOnboarding && (
        <Onboarding onComplete={() => setShowOnboarding(false)} />
      )}
    </>
  );
};

export default Home; 